import { Routes, Route, NavLink, useLocation } from 'react-router-dom'
import { useEffect, useState } from 'react'
import { Menu, Search, Settings, X } from 'lucide-react'
import DashboardPage from './pages/DashboardPage'
import ScanPage from './pages/ScanPage'
import StationPage from './pages/StationPage'
import StockStationPage from './pages/StockStationPage'
import InventoryPage from './pages/InventoryPage'
import OrdersPage from './pages/OrdersPage'
import ShippingPage from './pages/ShippingPage'
import TotesPage from './pages/TotesPage'
import CustomersPage from './pages/CustomersPage'
import CrmPage from './pages/CrmPage'
import ContactDetailPage from './pages/ContactDetailPage'
import CustomerInboxPage from './pages/CustomerInboxPage'
import SettingsPage from './pages/SettingsPage'
import WeeklyDigestAutoSend from './pages/ui/WeeklyDigestAutoSend'
import OrderImportAutoRun from './pages/ui/OrderImportAutoRun'
import TierReviewAutoRun from './pages/ui/TierReviewAutoRun'
import HubSearch, { HubSearchButton } from './pages/ui/HubSearch'
import { openHubSearch } from './utils/hubSearchEvents'
import { Toaster } from './lib/shadcn/sonner'
import { useRetoolPageTag } from './hooks/useRetoolPageTag'
import { useMobileTableLabels } from './hooks/useMobileTableLabels'
import { useHubAlerts } from './hooks/useHubAlerts'
import './mobile.css'
import './dialogs.css'
import { addHomeScreenAppTags } from './utils/homeScreenApp'
import { setHubIcons } from './utils/favicon'

addHomeScreenAppTags()
setHubIcons()

const NAV_GROUPS = [
  { label: 'Overview', items: [{ to: '/', label: 'Dashboard' }] },
  { label: 'Operations', items: [
    { to: '/orders', label: 'Orders' },
    { to: '/scan', label: 'Scan' },
    { to: '/shipping', label: 'Shipping' },
    { to: '/production', label: 'Production' },
    { to: '/totes', label: 'Totes' },
  ] },
  { label: 'Sales', items: [
    { to: '/crm', label: 'Leads' },
    { to: '/customers', label: 'Customers' },
    { to: '/inbox', label: 'Inbox' },
    { to: '/tier-review', label: 'Tier review' },
  ] },
  { label: 'Catalog', items: [
    { to: '/products', label: 'Products' },
    { to: '/ingredients', label: 'Ingredients' },
  ] },
]

function isNavigationActive(to: string, pathname: string) {
  if (to === '/products') return pathname === '/products' || pathname === '/inventory'
  if (to === '/customers') return pathname === '/customers' || pathname.startsWith('/customers/') || pathname.startsWith('/crm/customers/')
  if (to === '/crm') return pathname === '/crm' || pathname.startsWith('/crm/leads/')
  return pathname === to
}

export default function App() {
  const pageTag = useRetoolPageTag()
  useMobileTableLabels()
  const location = useLocation()
  const [menuOpen, setMenuOpen] = useState(false)
  // Bag reorder and seasonal alerts are listed in the Dashboard's Today
  // section; the Dashboard link shows how many are waiting.
  const hubAlerts = useHubAlerts()

  // Close the phone menu whenever the route changes.
  useEffect(() => {
    setMenuOpen(false)
  }, [location.pathname])

  const navLinks = NAV_GROUPS.map(group => (
    <div key={group.label} className="space-y-1">
      <h2 className="px-3 pt-3 pb-1 text-xs font-semibold uppercase tracking-wide text-muted-foreground">{group.label}</h2>
      {group.items.map(item => {
        const active = isNavigationActive(item.to, location.pathname)
        return (
          <NavLink key={item.to} to={item.to} end={item.to === '/' || item.to === '/crm'}
            aria-current={active ? 'page' : false}
            className={`flex items-center justify-between rounded-md px-3 py-2 text-sm font-medium transition-colors focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ring max-lg:py-3 max-lg:text-base ${active ? 'bg-primary text-primary-foreground' : 'text-muted-foreground hover:text-foreground hover:bg-accent'}`}>
            {item.label}
            {item.to === '/' && hubAlerts.count > 0 && (
              <span className="ml-2 inline-flex min-w-5 h-5 px-1.5 items-center justify-center rounded-full bg-destructive text-[11px] font-semibold text-white"
                aria-label={`${hubAlerts.count} dashboard alerts`}>{hubAlerts.count}</span>
            )}
          </NavLink>
        )
      })}
    </div>
  ))

  // Scan Station: full screen for the packing-table iPad, with no Hub header,
  // menu or search. Order import and the weekly digest still run in the background like any page.
  if (location.pathname === '/station') {
    return (
      <div className="min-h-screen flex flex-col bg-background text-foreground">
        <WeeklyDigestAutoSend />
        <OrderImportAutoRun />
        <main className="flex-1 overflow-auto">
          <StationPage />
        </main>
        <Toaster />
      </div>
    )
  }

  // Stock Station: full screen phone view for updating stock (unbagged
  // batch, bag weights, bagged counts), like the Scan Station.
  if (location.pathname === '/stock') {
    return (
      <div className="min-h-screen flex flex-col bg-background text-foreground">
        <OrderImportAutoRun />
        <main className="flex-1 overflow-auto">
          <StockStationPage />
        </main>
        <Toaster />
      </div>
    )
  }

  return (
    <div className="min-h-screen flex flex-col bg-background text-foreground">
      <header className="bg-background border-b border-border shrink-0">
        <div className="px-4 py-3 flex items-center gap-6 max-md:gap-2 max-md:py-2">
          <button
            type="button"
            aria-label={menuOpen ? 'Close menu' : 'Open menu'}
            aria-expanded={menuOpen}
            aria-controls="mobile-navigation"
            onClick={() => setMenuOpen((open) => !open)}
            className="lg:hidden relative -ml-2 p-2.5 rounded-md text-foreground hover:bg-accent"
          >
            {menuOpen ? <X className="w-5 h-5" /> : <Menu className="w-5 h-5" />}
            {!menuOpen && hubAlerts.count > 0 && (
              <span className="absolute top-1.5 right-1.5 h-2.5 w-2.5 rounded-full bg-destructive" />
            )}
          </button>
          <div className="flex flex-col leading-tight">
            <span className="font-bold text-lg tracking-tight text-foreground whitespace-nowrap">
              Poppin&apos; Hub
            </span>
            <span className="text-xs text-muted-foreground">Version {pageTag}</span>
          </div>
          <div className="ml-auto flex items-center gap-1">
            <div className="hidden xl:block">
              <HubSearchButton />
            </div>
            <div className="xl:hidden">
              <HubSearchButton compact />
            </div>
          </div>
          <NavLink
            to="/settings"
            aria-label="Settings"
            className={({ isActive }) =>
              `p-2 max-md:p-2.5 rounded-md transition-colors ${
                isActive
                  ? 'bg-primary text-primary-foreground'
                  : 'text-muted-foreground hover:text-foreground hover:bg-accent'
              }`
            }
          >
            <Settings className="w-5 h-5" />
          </NavLink>
        </div>
        {menuOpen && (
          <nav id="mobile-navigation" aria-label="Main navigation" onKeyDown={event => { if (event.key === 'Escape') { setMenuOpen(false); document.querySelector<HTMLButtonElement>('[aria-controls="mobile-navigation"]')?.focus() } }} className="lg:hidden max-h-[70dvh] overflow-y-auto space-y-2 px-3 pb-4 border-t border-border pt-2">
            <button
              type="button"
              onClick={() => {
                setMenuOpen(false)
                openHubSearch()
              }}
              className="flex items-center gap-2 rounded-md border border-input px-4 py-3 text-base text-muted-foreground hover:bg-accent hover:text-foreground"
            >
              <Search className="w-5 h-5" />
              Search orders, customers, products…
            </button>
            {navLinks}
          </nav>
        )}
      </header>

      <WeeklyDigestAutoSend />
      <OrderImportAutoRun />
      <TierReviewAutoRun />
      <HubSearch />

      <div className="flex flex-1 min-w-0">
        <aside className="hidden lg:block w-52 xl:w-56 shrink-0 border-r border-border bg-card">
          <nav aria-label="Main navigation" className="sticky top-0 max-h-screen overflow-y-auto space-y-2 px-3 pb-4">{navLinks}</nav>
        </aside>
        <main className="flex-1 min-w-0 overflow-x-auto">
        <Routes>
          <Route path="/" element={<DashboardPage />} />
          <Route path="/scan" element={<ScanPage station title="Scan" />} />
          <Route path="/orders" element={<OrdersPage />} />
          <Route path="/shipping" element={<ShippingPage />} />
          <Route path="/customers" element={<CustomersPage />} />
          <Route path="/crm" element={<CrmPage />} />
          <Route path="/inbox" element={<CustomerInboxPage />} />
          <Route path="/crm/leads/:id" element={<ContactDetailPage kind="lead" />} />
          <Route path="/crm/customers/:id" element={<ContactDetailPage kind="customer" />} />
          <Route path="/customers/:id" element={<ContactDetailPage kind="customer" />} />
          <Route path="/products" element={<InventoryPage />} />
          <Route path="/inventory" element={<InventoryPage />} />
          <Route path="/ingredients" element={<InventoryPage />} />
          <Route path="/production" element={<InventoryPage />} />
          <Route path="/tier-review" element={<InventoryPage />} />
          <Route path="/totes" element={<TotesPage />} />
          <Route path="/settings" element={<SettingsPage />} />
        </Routes>
        </main>
      </div>

      <Toaster />
    </div>
  )
}
